/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import { BusinessRole, IBusiness } from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Collect team-management API surface (business-admin only).
// All endpoints require the tenant header (`x-business-id`) plus the business
// session (attached automatically by the global axios interceptor).
// ─────────────────────────────────────────────────────────────────────────

export interface ITeamInvite {
    _id: string;
    businessId: string;
    email: string;
    roleName: string;
    status: string;
    createdAt?: string;
    expiresAt?: string;
}

export interface ITeamMember {
    _id: string;
    businessId: string;
    role: BusinessRole;
    isActive?: boolean;
    businessUserId?: {
        _id: string;
        email: string;
        firstName: string;
        lastName: string;
        status: string;
    } | null;
    businessRoleId?: { roleName: string } | null;
    createdAt?: string;
}

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        withCredentials: true,
        headers: { ...(extra.headers || {}), "x-business-id": businessId },
    };
}

function pickError(error: any, fallback: string): string {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
}

export const inviteTeamMemberApi = async (
    businessId: string,
    email: string,
    expiresInDays?: number,
): Promise<{ invite: ITeamInvite; businessId: string }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/team/invite`,
            { email, expiresInDays },
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to send invite."));
    }
};

export const listTeamInvitesApi = async (
    businessId: string,
): Promise<ITeamInvite[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/team/invites`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invites."));
    }
};

export const revokeTeamInviteApi = async (
    businessId: string,
    inviteId: string,
): Promise<void> => {
    try {
        await axios.delete(
            `${API_BASE_URL}/collect/team/invites/${inviteId}`,
            withBusiness(businessId),
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to revoke invite."));
    }
};

export const listTeamMembersApi = async (
    businessId: string,
): Promise<ITeamMember[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/team/members`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load team members."));
    }
};

export const removeTeamMemberApi = async (
    businessId: string,
    membershipId: string,
): Promise<void> => {
    try {
        await axios.delete(
            `${API_BASE_URL}/collect/team/members/${membershipId}`,
            withBusiness(businessId),
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to remove member."));
    }
};

export const updateTeamMemberRoleApi = async (
    businessId: string,
    membershipId: string,
    role: "admin" | "staff",
): Promise<void> => {
    try {
        await axios.put(
            `${API_BASE_URL}/collect/team/members/${membershipId}/role`,
            { role },
            withBusiness(businessId),
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update member role."));
    }
};

// Re-export for callers that want the shared business type.
export type { IBusiness };
